let coolPropPromise = null;

export const COOLPROP_SCRIPT_URL = '/coolprop.js';
export const COOLPROP_WASM_URL = '/coolprop.wasm';

const PA_TO_BAR = 1 / 100000;
const J_TO_KJ = 1 / 1000;

function getModule() {
  return window.Module;
}

export function loadCoolProp() {
  if (coolPropPromise) {
    return coolPropPromise;
  }

  coolPropPromise = new Promise((resolve, reject) => {
    const existingModule = getModule();

    if (existingModule && typeof existingModule.PropsSI === 'function') {
      resolve(existingModule);
      return;
    }

    const existingScript = document.querySelector('script[data-coolprop="true"]');

    const waitUntilReady = function waitUntilReady() {
      const moduleRef = getModule();

      if (moduleRef && typeof moduleRef.PropsSI === 'function') {
        resolve(moduleRef);
        return;
      }

      setTimeout(waitUntilReady, 50);
    };

    if (existingScript) {
      waitUntilReady();
      return;
    }

    window.Module = window.Module || {};

    window.Module.locateFile = function locateFile(path) {
      if (path && path.endsWith('.wasm')) {
        return COOLPROP_WASM_URL;
      }
      return path;
    };

    window.Module.onRuntimeInitialized = function onRuntimeInitialized() {
      const moduleRef = getModule();

      if (moduleRef && typeof moduleRef.PropsSI === 'function') {
        resolve(moduleRef);
        return;
      }

      reject(new Error('CoolProp carregou, mas PropsSI não ficou disponível.'));
    };

    const script = document.createElement('script');
    script.src = COOLPROP_SCRIPT_URL;
    script.async = true;
    script.setAttribute('data-coolprop', 'true');

    script.onerror = function onError() {
      reject(new Error('Falha ao carregar /coolprop.js'));
    };

    document.body.appendChild(script);
  });

  return coolPropPromise;
}

export async function propsSI(output, name1, prop1, name2, prop2, fluid) {
  const moduleRef = await loadCoolProp();

  if (!moduleRef || typeof moduleRef.PropsSI !== 'function') {
    throw new Error('PropsSI não disponível no CoolProp.');
  }

  return moduleRef.PropsSI(output, name1, prop1, name2, prop2, fluid);
}

export async function safePropsSI(output, name1, prop1, name2, prop2, fluid) {
  try {
    const value = await propsSI(output, name1, prop1, name2, prop2, fluid);

    if (!Number.isFinite(value)) {
      return null;
    }

    return value;
  } catch (error) {
    return null;
  }
}

export async function getFluidTemperatureLimits(fluid) {
  try {
    const Tcrit =
      (await safePropsSI('Tcrit', '', 0, '', 0, fluid)) ||
      (await safePropsSI('T_critical', '', 0, '', 0, fluid));

    const Ttriple =
      (await safePropsSI('T_triple', '', 0, '', 0, fluid)) ||
      (await safePropsSI('Ttriple', '', 0, '', 0, fluid));

    if (!Tcrit || !Ttriple) {
      return null;
    }

    return {
      Tcrit,
      Ttriple,
    };
  } catch (error) {
    return null;
  }
}

export async function getSaturationPoint(T, fluid) {
  const P = await safePropsSI('P', 'T', T, 'Q', 0, fluid);
  const hLiq = await safePropsSI('H', 'T', T, 'Q', 0, fluid);
  const hVap = await safePropsSI('H', 'T', T, 'Q', 1, fluid);

  if (P == null || hLiq == null || hVap == null) {
    return null;
  }

  return {
    p: P * PA_TO_BAR,
    hLiq: hLiq * J_TO_KJ,
    hVap: hVap * J_TO_KJ,
  };
}

export async function getIsothermEnthalpy(pBar, TK, fluid) {
  const pPa = pBar * 100000;
  const h = await safePropsSI('H', 'P', pPa, 'T', TK, fluid);

  if (h == null) {
    return null;
  }

  return h * J_TO_KJ;
}